import { buildHierarchyRows } from "./valueListHierarchy";
export const MOVE_DIRECTIONS = ["up", "down", "top", "bottom"];
function moveIndex(index, last, direction) {
    switch (direction) {
        case "up":
            return Math.max(0, index - 1);
        case "down":
            return Math.min(last, index + 1);
        case "top":
            return 0;
        case "bottom":
            return last;
    }
}
function moved(items, from, to) {
    const next = [...items];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
}
// ── Manual order ────────────────────────────────────────────────────────────
/** The blocks a manual list moves: a category with its sub-values, in display order. */
function families(values, locale) {
    const result = [];
    for (const row of buildHierarchyRows(values, "Manual", locale)) {
        if (row.parent === null)
            result.push([row.value]);
        else
            result[result.length - 1].push(row.value);
    }
    return result;
}
function familyIndex(blocks, internalCode) {
    return blocks.findIndex((block) => block.some((value) => value.internalCode === internalCode));
}
/** The codes that move with `internalCode`: its whole family, category first. */
export function familyCodes(values, internalCode, locale) {
    const blocks = families(values, locale);
    return (blocks[familyIndex(blocks, internalCode)] ?? []).map((value) => value.internalCode);
}
/** Whether moving the family of `internalCode` that way changes anything (it is not already at that end). */
export function canMoveFamily(values, internalCode, direction, locale) {
    const blocks = families(values, locale);
    const index = familyIndex(blocks, internalCode);
    return index >= 0 && moveIndex(index, blocks.length - 1, direction) !== index;
}
/**
 * The values once the family of `internalCode` (a category or one of its sub-values) moved as one
 * block. The orders the list already uses are dealt out again in the new display order, so only
 * the values between the old and the new place change; duplicated orders are renumbered 1..n.
 */
export function moveFamily(values, internalCode, direction, locale) {
    const blocks = families(values, locale);
    const index = familyIndex(blocks, internalCode);
    if (index < 0)
        return [...values];
    const order = moved(blocks, index, moveIndex(index, blocks.length - 1, direction)).flat();
    const slots = order.map((value) => value.sortOrder).sort((a, b) => a - b);
    const distinct = slots.every((slot, i) => i === 0 || slot > slots[i - 1]);
    const target = new Map(order.map((value, i) => [value.internalCode, distinct ? slots[i] : i + 1]));
    return values.map((value) => {
        const sortOrder = target.get(value.internalCode) ?? value.sortOrder;
        return sortOrder === value.sortOrder ? value : { ...value, sortOrder };
    });
}
/**
 * The values in the order Eudonet declares: each system value goes back to its default order, and
 * Custom values (which have none) follow them, in their current order. Custom values already placed
 * after the system ones keep their orders, and a list that declares no order is left as it is, so
 * a restore never records a change the table does not show.
 */
export function withDefaultOrder(values) {
    const declared = values.flatMap((value) => (value.defaultSortOrder === null ? [] : [value.defaultSortOrder]));
    if (declared.length === 0)
        return [...values];
    const after = Math.max(...declared);
    const custom = values
        .filter((value) => value.defaultSortOrder === null)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.internalCode.localeCompare(b.internalCode));
    const keepCustom = custom.length === 0 || custom[0].sortOrder > after;
    const customOrder = new Map(custom.map((value, i) => [value.internalCode, keepCustom ? value.sortOrder : after + i + 1]));
    return values.map((value) => {
        const sortOrder = value.defaultSortOrder ?? customOrder.get(value.internalCode) ?? value.sortOrder;
        return sortOrder === value.sortOrder ? value : { ...value, sortOrder };
    });
}
/** Whether restoring the default order would change nothing. */
export function isDefaultOrder(values) {
    const restored = withDefaultOrder(values);
    return restored.every((value, i) => value === values[i]);
}
// ── Pins ────────────────────────────────────────────────────────────────────
/** The pinned values, by rank. */
export function pinnedValues(values) {
    return values
        .filter((value) => value.pinRank !== null)
        .sort((a, b) => (a.pinRank ?? 0) - (b.pinRank ?? 0));
}
/** The values with the pins ranked 1..n in the given order, every other value unpinned. */
function withPins(values, pinned) {
    const rank = new Map(pinned.map((code, i) => [code, i + 1]));
    return values.map((value) => {
        const pinRank = rank.get(value.internalCode) ?? null;
        return pinRank === value.pinRank ? value : { ...value, pinRank };
    });
}
/** A new pin goes to the bottom of the pinned block, never above a value already pinned. */
export function pin(values, internalCode) {
    const pinned = pinnedValues(values).map((value) => value.internalCode);
    return pinned.includes(internalCode) ? [...values] : withPins(values, [...pinned, internalCode]);
}
/** Unpins a value; the pins below it move up one rank. */
export function unpin(values, internalCode) {
    const pinned = pinnedValues(values).map((value) => value.internalCode);
    return withPins(values, pinned.filter((code) => code !== internalCode));
}
/** Whether moving the pin of `internalCode` that way changes anything. */
export function canMovePin(values, internalCode, direction) {
    const pinned = pinnedValues(values);
    const index = pinned.findIndex((value) => value.internalCode === internalCode);
    return index >= 0 && moveIndex(index, pinned.length - 1, direction) !== index;
}
/** Moves a pinned value among the pins only; the rest of the list is untouched. */
export function movePin(values, internalCode, direction) {
    const pinned = pinnedValues(values).map((value) => value.internalCode);
    const index = pinned.indexOf(internalCode);
    if (index < 0)
        return [...values];
    return withPins(values, moved(pinned, index, moveIndex(index, pinned.length - 1, direction)));
}
